import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import type { MatchDto } from "@/types/api";
import { statsApi } from "@/api/stats";
import { matchesApi } from "@/api/matches";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { RequireContext, ErrorState, PageLoader, EmptyState } from "@/components/StateViews";
import { Tabs } from "@/components/ui/tabs";

export function StatsPage() {
  return (
    <RequireContext>
      <Stats />
    </RequireContext>
  );
}

function Stats() {
  const [tab, setTab] = useState("me");
  return (
    <div className="space-y-4">
      <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
        <BarChart3 className="size-5 text-primary" /> Statisztika
      </h1>
      <Tabs
        items={[
          { value: "me", label: "Saját" },
          { value: "tournament", label: "Torna" },
        ]}
        value={tab}
        onValueChange={setTab}
      />
      {tab === "me" ? <PersonalStats /> : <TournamentStats />}
      <p className="text-xs text-muted-foreground">Tipp: egy csapat statisztikáit a meccskártyán a csapat nevére kattintva nézheted meg.</p>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/50 p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function PersonalStats() {
  const scope = useScope();
  const q = useQuery({ queryKey: ["personalStats", scope.leagueId, scope.tournamentId], queryFn: () => statsApi.personal(scope) });
  if (q.isLoading) return <PageLoader />;
  if (q.error) return <ErrorState error={q.error} />;
  const s = q.data!;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      <Tile label="Összpont" value={s.totalPoints} />
      <Tile label="Helyezés" value={s.rank ? `#${s.rank}` : "—"} sub={`${s.totalParticipants} játékosból`} />
      <Tile label="Telitalálat" value={s.bullseyeCount} />
      <Tile label="Aktuális sorozat" value={s.currentStreak} sub={`Leghosszabb: ${s.longestStreak}`} />
      <Tile label="Pontozott tippek" value={s.scoredTips} />
      <Tile label="Függő tippek" value={s.pendingTips} />
      <Tile label="Részleges" value={s.partialCount} />
      <Tile label="Pont nélküli" value={s.missCount} />
    </div>
  );
}

function TournamentStats() {
  const scope = useScope();
  const q = useQuery({ queryKey: ["matches", scope.leagueId, scope.tournamentId], queryFn: () => matchesApi.list(scope) });

  const stats = useMemo(() => {
    const played = (q.data ?? []).filter((m) => m.scoreA !== null && m.scoreB !== null);
    const n = played.length;
    let home = 0, draw = 0, away = 0, goals = 0;
    let biggest: MatchDto | null = null;
    let biggestDiff = -1;
    for (const m of played) {
      const a = m.scoreA!, b = m.scoreB!;
      goals += a + b;
      if (a > b) home++;
      else if (a < b) away++;
      else draw++;
      const diff = Math.abs(a - b);
      if (diff > biggestDiff) { biggestDiff = diff; biggest = m; }
    }
    return { total: q.data?.length ?? 0, played: n, home, draw, away, goals, avg: n ? goals / n : 0, biggest };
  }, [q.data]);

  if (q.isLoading) return <PageLoader />;
  if (q.error) return <ErrorState error={q.error} />;
  if (stats.played === 0)
    return <EmptyState title="Még nincs lejátszott mérkőzés" description="A torna-statisztika a lejátszott meccsek eredményeiből számol." icon={<BarChart3 className="size-7" />} />;

  const p = (v: number) => (stats.played ? `${Math.round((v / stats.played) * 100)}%` : "0%");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        <Tile label="Mérkőzések" value={stats.played} sub={`${stats.total} összesen`} />
        <Tile label="Összes gól" value={stats.goals} />
        <Tile label="Átlag gól / meccs" value={stats.avg.toFixed(2)} />
        <Tile label="Hazai győzelem" value={p(stats.home)} sub={`${stats.home} meccs`} />
        <Tile label="Döntetlen" value={p(stats.draw)} sub={`${stats.draw} meccs`} />
        <Tile label="Vendég győzelem" value={p(stats.away)} sub={`${stats.away} meccs`} />
      </div>
      {stats.biggest && (
        <div className="rounded-xl border border-border bg-card/50 p-3">
          <div className="mb-1.5 text-xs uppercase tracking-wide text-muted-foreground">Legnagyobb különbség</div>
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <TeamFlag name={stats.biggest.teamAName} flag={stats.biggest.teamAFlag} className="size-5" />
            {stats.biggest.teamAName}
            <span className="mx-1 font-bold tabular-nums">{stats.biggest.scoreA}–{stats.biggest.scoreB}</span>
            {stats.biggest.teamBName}
            <TeamFlag name={stats.biggest.teamBName} flag={stats.biggest.teamBFlag} className="size-5" />
          </div>
        </div>
      )}
    </div>
  );
}
